/**
 * Guided product-tour storyboard.
 *
 * Each step describes ONE spotlight:
 *   path      — react-router route the tour must be on for this step
 *   selector  — CSS selector for the target DOM element
 *   title     — short callout heading
 *   body      — one-sentence explanation
 *   placement — where to float the tooltip: "top" | "bottom" | "left" | "right" | "auto"
 *   optional  — when true, tour silently skips this step if the target is missing
 *               (e.g. Dream detail card doesn't exist for a fresh account)
 *
 * The tour engine (`DemoTour.jsx`) navigates path→path, waits for the
 * selector to mount, then renders the spotlight. Users move with the
 * arrow controls, keyboard, or Esc to exit.
 */
export const DEMO_TOUR_STEPS = [
  // ─── EXPLORE ─────────────────────────────────────────────────────
  {
    path: "/explore",
    selector: '[data-tour="explore-hero"]',
    tab: "explore",
    title: "Where to next?",
    body: "Every session starts with the big question. This is where you discover destinations that match your vibe.",
    placement: "bottom",
  },
  {
    path: "/explore",
    selector: '[data-tour="explore-booked-shortcut"]',
    tab: "explore",
    title: "Your booked trips",
    body: "One-tap access to trips you've already booked, so you can plan and organize them.",
    placement: "bottom",
    optional: true,
  },
  {
    path: "/explore",
    selector: '[data-tour="explore-chip-row"]',
    tab: "explore",
    title: "Filter by vibe",
    body: "Pick Hotels, Resorts, Camping and more. Tap the filter icon on the right for all 16 categories.",
    placement: "bottom",
  },
  {
    path: "/explore",
    selector: '[data-tour="explore-location-fields"]',
    tab: "explore",
    title: "Where you're going",
    body: "Any combination of City, State, Country. Type VT in State for Vermont, or CA in Country for Canada.",
    placement: "top",
  },
  {
    path: "/explore",
    selector: '[data-tour="explore-budget"]',
    tab: "explore",
    title: "Budget",
    body: "Choose a spending range — every card you see respects it.",
    placement: "top",
    optional: true,
  },
  {
    path: "/explore",
    selector: '[data-tour="explore-first-match"]',
    tab: "explore",
    title: "Peek the first match",
    body: "Before you start swiping, see if the first result looks right.",
    placement: "top",
    optional: true,
  },
  {
    path: "/explore",
    selector: '[data-tour="explore-start-swiping"]',
    tab: "explore",
    title: "Start swiping",
    body: "Swipe right to save, left to skip. Undo is always one tap away.",
    placement: "top",
  },

  // ─── DREAM ───────────────────────────────────────────────────────
  {
    path: "/dream",
    selector: '[data-tour="dream-header"]',
    tab: "dream",
    title: "Your dream vacations",
    body: "A collection of destinations waiting for their perfect moment.",
    placement: "bottom",
  },
  {
    path: "/dream",
    selector: '[data-tour="dream-list"]',
    tab: "dream",
    title: "Plan any dream",
    body: "Tap a card to open the planner — pins, budget, itinerary all live inside.",
    placement: "top",
    optional: true,
  },
  {
    path: "/dream",
    selector: '[data-tour="dream-new"]',
    tab: "dream",
    title: "Or start fresh",
    body: "Add a new dream vacation from scratch.",
    placement: "top",
    optional: true,
  },

  // ─── CHAPTERS ────────────────────────────────────────────────────
  {
    path: "/chapters",
    selector: '[data-tour="chapters-header"]',
    tab: "chapters",
    title: "Your chapters",
    body: "One chapter per year, filled with the stories from your travels.",
    placement: "bottom",
  },
  {
    path: "/chapters",
    selector: '[data-tour="chapters-first-card"]',
    tab: "chapters",
    title: "Tap a year",
    body: "See every story from that year. The cover auto-populates from your most recent trip — you can override it.",
    placement: "top",
    optional: true,
  },
  {
    path: "/chapters",
    selector: '[data-tour="chapters-new"]',
    tab: "chapters",
    title: "Add a new chapter",
    body: "Plan ahead — chapters can hold future years too.",
    placement: "top",
    optional: true,
  },

  // ─── PASSPORT ────────────────────────────────────────────────────
  {
    path: "/passport",
    selector: '[data-tour="passport-header"]',
    tab: "passport",
    title: "Your passport",
    body: "Every trip you complete earns a stamp. Watch your passport fill up.",
    placement: "bottom",
  },
  {
    path: "/passport",
    selector: '[data-tour="passport-rewards"]',
    tab: "passport",
    title: "Unlock rewards",
    body: "The more you travel, the more perks you unlock.",
    placement: "top",
    optional: true,
  },

  // ─── LOUNGE ──────────────────────────────────────────────────────
  {
    path: "/lounge",
    selector: '[data-tour="lounge-header"]',
    tab: "lounge",
    title: "The traveler's lounge",
    body: "Share stories, tips, and photos with the community.",
    placement: "bottom",
  },
  {
    path: "/lounge",
    selector: '[data-tour="lounge-composer"]',
    tab: "lounge",
    title: "Post a story",
    body: "Add photos, hashtags, and tag other travelers. Like and comment — same as any social feed.",
    placement: "top",
    optional: true,
  },
  {
    path: "/lounge",
    selector: '[data-testid="nav-search-btn"]',
    tab: "lounge",
    title: "Find other travelers",
    body: "Use the search icon in the header to look up users by @handle or name — see their posts or start a chat.",
    placement: "left",
    optional: true,
  },

  // ─── FINALE ──────────────────────────────────────────────────────
  {
    path: "/home",
    selector: '[data-tour="home-hero"]',
    tab: "home",
    title: "You're all set!",
    body: "That's the whirlwind tour. Every tab is one tap away from the side nav — go build your own travel bio.",
    placement: "bottom",
    optional: true,
  },
];
